<template>
  <v-stepper v-model="currentStep" alt-labels class="elevation-0">
    <h1
      class="text-h4 font-weight-bold text-center"
      style="text-decoration: underline"
    >
      Débriefing après l'activité
    </h1>
    <v-stepper-items>
      <v-stepper-content
        v-for="(step, index) in steps"
        :key="`content-${index}`"
        :step="index + 1"
      >
        <v-card flat class="text-center" style="transition: none">
          <div
            v-if="step.image || step.image2"
            class="d-flex justify-center mb-4 images-container"
          >
            <v-img
              v-if="step.image"
              :src="getImageUrl(step.image)"
              min-height="50vh"
              max-height="55vh"
              contain
            ></v-img>
            <v-img
              v-if="step.image2"
              :src="getImageUrl(step.image2)"
              min-height="50vh"
              max-height="55vh"
              contain
            ></v-img>
          </div>

          <!-- TEXT ONLY -->
          <v-card-text
            v-if="step.type === 'text-only'"
            class="text-center black--text"
          >
            <div v-if="index === 0">
              <h2
                class="text-h4 font-weight-bold mb-16"
                style="text-decoration: underline"
              >
                {{ step.title }}
              </h2>
              <p
                v-if="step.content"
                class="mb-12"
                style="font-size: 24px; line-height: 40px"
              >
                {{ step.content }}
              </p>
              <p
                v-if="step.content2"
                class="mb-12"
                style="font-size: 24px; line-height: 40px"
              >
                {{ step.content2 }}
              </p>
              <p
                v-if="step.content3"
                class="mb-16"
                style="font-size: 24px; line-height: 40px"
              >
                {{ step.content3 }}
              </p>
            </div>
            <div v-else>
              <h2
                v-if="step.title"
                class="text-h5 font-weight-bold mb-4"
                style="text-decoration: underline"
              >
                {{ step.title }}
              </h2>
              <p style="font-size: 24px; line-height: 40px">
                {{ step.content }}
              </p>
            </div>
          </v-card-text>

          <!-- RADIO -->
          <v-card-text
            v-else-if="step.type === 'radio'"
            class="d-flex align-center grey lighten-4"
            style="min-height: auto; padding: 8px 16px"
          >
            <span class="text-h6 mb-0 mr-4" style="line-height: 1.2">{{
              step.question
            }}</span>
            <div
              class="d-flex"
              :style="{
                marginTop: '-4px',
                marginLeft: step.radioSpacing || '20px',
              }"
            >
              <v-radio-group
                v-model="answers[currentStep - 1]"
                row
                class="mb-0"
                hide-details
                style="width: auto"
              >
                <div
                  v-for="(option, i) in step.options"
                  :key="option"
                  class="radio-option-wrapper"
                  :style="{
                    marginRight:
                      step.radioOptionSpacing && i < step.options.length - 1
                        ? step.radioOptionSpacing
                        : '0',
                  }"
                >
                  <div class="radio-image-container">
                    <v-img
                      v-if="step.radioImages && step.radioImages[i]"
                      :src="getImageUrl(step.radioImages[i])"
                      contain
                      max-height="35"
                      max-width="35"
                      class="mb-1"
                    />
                    <span v-else class="radio-number">
                      {{ i + 1 }}
                    </span>
                  </div>
                  <v-radio :value="option" hide-details class="radio-center" />
                </div>
              </v-radio-group>
            </div>
          </v-card-text>

          <!-- TEXT INPUT -->
          <v-card-text v-else-if="step.type === 'text'">
            <div class="text-h6 mb-4 text-center">{{ step.question }}</div>
            <v-textarea
              v-model="answers[index]"
              :label="step.placeholder || 'Votre réponse...'"
              outlined
              class="mx-auto"
              style="width: 100%"
              counter="300"
              maxlength="300"
              rows="5"
              auto-grow
            />
          </v-card-text>

          <!-- FINAL -->
          <v-card-text v-else-if="step.type === 'final'" class="text-center">
            <p
              v-if="step.content"
              class="mb-12"
              style="font-size: 24px; line-height: 40px"
            >
              {{ step.content }}
            </p>
          </v-card-text>

          <v-card-actions class="justify-center">
            <v-btn
              v-if="currentStep > 1 && currentStep < steps.length"
              text
              @click="currentStep--"
            >
              Précédent
            </v-btn>
            <v-btn
              v-if="currentStep === 1"
              color="success"
              class="white--text"
              @click="nextStep"
            >
              Commencer le debriefing
            </v-btn>
            <v-btn
              v-else-if="currentStep === steps.length"
              v-show="false"
              color="success"
              class="white--text"
              @click="goHome"
            >
              Retour à l'accueil
            </v-btn>
            <v-btn
              v-else-if="currentStep === steps.length - 1"
              color="success"
              class="white--text"
              :disabled="!isValid"
              @click="nextStep()"
            >
              Terminer l'auto-débriefing
            </v-btn>
            <v-btn
              v-else
              color="primary"
              :disabled="!isValid"
              @click="nextStep"
            >
              Suivant
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-stepper-content>
    </v-stepper-items>
  </v-stepper>
</template>

<script>
import { mapActions } from "vuex";
import { assetsUrl } from "@/env";

export default {
  name: "AutoDebriefing",

  props: {
    sessionId: {
      type: String,
      required: true,
    },
    sequenceId: {
      type: String,
      required: true,
    },
  },

  watch: {
    currentStep(val) {
      if (val === this.steps.length) {
        this.sendAutoDebriefing();
      }
    },
  },

  data() {
    return {
      currentStep: 1,
      answers: {},
      steps: [
        {
          type: "text-only",
          title: "",
          image: "",
          content:
            "Vous allez maintenant réfléchir à ce qu'il s'est passé dans votre équipe pendant l'activité.",
          content2:
            "L'objectif n'est pas de juger si vous avez bien travaillé ou non, mais de comprendre ce qui s'est réellement passé au sein de votre équipe.",
          content3:
            "Pour savoir ce que vous changeriez si vous deviez travailler ensemble à nouveau.",
        },
        {
          type: "radio",
          title: "",
          image: "AutoDebrief/AD1.png",
          question: "Votre ressenti",
          options: [1, 2, 3, 4, 5],
          radioSpacing: "323px",
          radioOptionSpacing: "58px",
          radioImages: [
            "AutoDebrief/g33.svg",
            "AutoDebrief/g34.svg",
            "AutoDebrief/g35.svg",
            "AutoDebrief/g36.svg",
            "AutoDebrief/g37.svg",
          ],
        },
        {
          type: "text",
          title: "",
          image: "AutoDebrief/ADNew.png",
          question:
            "Écrivez dans quel état émotionnel vous étiez pendant l’activité :",
          placeholder: "Votre réponse...",
        },
        {
          type: "text-only",
          title: "",
          image: "AutoDebrief/AD4.png",
          content:
            "Regardons maintenant ensemble comment s'est déroulée l'activité",
        },
        {
          type: "text-only",
          title: "",
          image: "AutoDebrief/AD6.png",
          content: "",
        },
        {
          type: "text",
          title: "",
          image: "AutoDebrief/AD7G.png",
          image2: "AutoDebrief/AD7D.png",
          question:
            "Expliquez comment vous avez réalisé l'activité dans votre équipe :",
          placeholder: "Votre réponse...",
        },
        {
          type: "text-only",
          title: "",
          image: "AutoDebrief/AD8.png",
          content: "",
        },
        {
          type: "text",
          title: "",
          image: "AutoDebrief/AD9.png",
          question:
            "Expliquez comment vous avez communiqué dans votre équipe :",
          placeholder: "Votre réponse...",
        },
        {
          type: "text",
          title: "",
          image: "AutoDebrief/AD10.png",
          question: "Ecrivez votre réponse ci-dessous :",
          placeholder: "Votre réponse...",
        },
        {
          type: "final",
          title: "",
          image: "",
          content: "Merci pour vos réponses !",
        },
      ],
    };
  },

  computed: {
    currentStepData() {
      return this.steps[this.currentStep - 1];
    },
    getImageUrl() {
      return (path) => {
        if (!path) return "";
        return `${assetsUrl}${path}`;
      };
    },

    isValid() {
      const currentAnswer = this.answers[this.currentStep - 1];
      if (
        this.currentStepData.type === "text-only" ||
        this.currentStepData.type === "final"
      ) {
        return true;
      }
      return !!currentAnswer;
    },
  },

  methods: {
    ...mapActions("session", ["sendAutoDebriefing"]),
    sendAutoDebriefing() {
      const formattedAnswers = {
        defusing_1: this.answers[1],
        defusing_2: this.answers[2],
        discovering_1: this.answers[5],
        discovering_2: this.answers[7],
        deepening_1: this.answers[8],
      };
      this.$store.dispatch("session/sendAutoDebriefing", {
        sessionId: this.sessionId,
        sequenceId: this.sequenceId,
        answers: formattedAnswers,
        next: (response) => {
          console.log("Auto-debriefing envoyé:", response);
        },
      });
      this.$emit("finished");
    },
    nextStep() {
      if (this.currentStep < this.steps.length) {
        this.currentStep++;
      } else {
        this.$emit("finished", this.answers);
      }
    },
    goHome() {
      this.$router.push({ name: "home" });
    },
    getRadioSpacingClass(step, index) {
      if (!step.radioSpacing) return "";
      return `mr-${step.radioSpacing}`;
    },
  },
};
</script>

<style scoped>
:deep(.text-input-tall textarea) {
  min-height: 300px !important;
}
</style>
<style>
.images-container {
  display: flex !important;
  flex-wrap: nowrap !important;
  justify-content: center !important;
}
.images-container .v-image {
  flex: 0 0 auto !important;
  max-width: 50% !important;
}
.v-stepper__content {
  padding: 10px !important;
}
.radio-option-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 58px;
}
.radio-image-container {
  width: 35px;
  display: flex;
  justify-content: center;
  margin-bottom: 4px;
}
.radio-image-container .v-img {
  width: 35px;
  height: 35px;
}
.v-radio-group {
  display: flex;
  justify-content: center;
  align-items: flex-start;
}
.v-radio-group .v-radio {
  margin: 0 !important;
  padding: 0 !important;
}
.radio-option-wrapper .v-radio {
  margin: 0 !important;
  display: flex;
  justify-content: center;
  width: 100%;
}
.radio-option-wrapper .v-input {
  margin: 0 !important;
  padding: 0 !important;
  display: flex;
  justify-content: center;
}
.radio-option-wrapper .v-input__control {
  display: flex;
  justify-content: center;
}
.radio-option-wrapper .v-radio .v-selection-control {
  margin: 0 !important;
  padding: 0 !important;
  display: flex;
  justify-content: center;
}
.radio-option-wrapper .v-input--selection-controls__input {
  display: flex;
  justify-content: center;
  margin-right: 0 !important;
}
.radio-option-wrapper .v-selection-control {
  margin-right: 0 !important;
}
.radio-option-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
}
.radio-image-container {
  width: 35px;
  display: flex;
  justify-content: center;
  margin-bottom: 4px;
}
.radio-image-container .v-img {
  width: 35px;
  height: 35px;
}
.radio-circle {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(0, 0, 0, 0.38);
  border-radius: 50%;
  cursor: pointer;
  display: block;
}
.radio-circle.checked {
  background-color: #4caf50;
  border-color: #4caf50;
}
.radio-circle.checked::after {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 8px;
  height: 8px;
  background-color: white;
  border-radius: 50%;
}
.radio-input-wrapper input[type="radio"] {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  width: 100%;
  height: 100%;
  margin: 0;
}
.radio-circle {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(0, 0, 0, 0.38);
  border-radius: 50%;
  transition: all 0.2s;
}
.radio-input-wrapper input[type="radio"]:checked + .radio-circle {
  background-color: #4caf50;
  border-color: #4caf50;
}
.radio-input-wrapper input[type="radio"]:checked + .radio-circle::after {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 8px;
  height: 8px;
  background-color: white;
  border-radius: 50%;
}
.radio-input-wrapper input[type="radio"] {
  position: absolute;
  opacity: 0;
  width: 18px;
  height: 18px;
  margin: 0;
  cursor: pointer;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
}
.radio-circle {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(0, 0, 0, 0.38);
  border-radius: 50%;
  display: block;
  cursor: pointer;
  position: relative;
}
.radio-input-wrapper input[type="radio"]:checked + .radio-circle {
  border-color: #4caf50;
  background-color: #4caf50;
}
.radio-input-wrapper input[type="radio"]:checked + .radio-circle::before {
  content: "";
  width: 8px;
  height: 8px;
  background-color: white;
  border-radius: 50%;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}
.radio-input-wrapper .v-input--radio .v-input__control .v-radio {
  margin: 0 !important;
}
.radio-input-wrapper
  .v-input--radio
  .v-input__control
  .v-radio
  .v-selection-control {
  margin: 0 !important;
  padding: 0 !important;
}
.radio-input-wrapper
  .v-input--radio
  .v-input__control
  .v-radio
  .v-selection-control
  .v-selection-control__input {
  display: flex;
  justify-content: center;
}
.radio-input-wrapper .v-input--radio .v-input__control .v-radio {
  margin: 0 !important;
  width: 24px !important;
  display: flex;
  justify-content: center;
}
.radio-input-wrapper
  .v-input--radio
  .v-input__control
  .v-radio
  .v-selection-control {
  margin: 0 !important;
  padding: 0 !important;
  width: 24px !important;
  display: flex;
  justify-content: center;
}
.radio-input-wrapper .v-input--radio .v-input__control .v-radio {
  margin: 0 !important;
}
.radio-input-wrapper
  .v-input--radio
  .v-input__control
  .v-radio
  .v-selection-control {
  margin: 0 !important;
  padding: 0 !important;
  width: 24px !important;
  display: flex;
  justify-content: center;
}
.radio-input-wrapper .v-input--radio .v-input__control .v-radio {
  margin: 0 !important;
  padding: 0 !important;
}
.radio-input-wrapper
  .v-input--radio
  .v-input__control
  .v-radio
  .v-selection-control {
  margin: 0 !important;
  padding: 0 !important;
}
.radio-input-wrapper .radio-custom {
  display: flex;
  justify-content: center;
}
.radio-input-wrapper .radio-custom .v-input {
  padding-top: 0;
  margin-top: 0;
}
.radio-input-wrapper .radio-custom .v-input__control {
  margin: 0;
}
.radio-input-wrapper
  .radio-custom
  .v-input--selection-controls
  .v-input__control {
  display: flex;
  justify-content: center;
}
.radio-input-wrapper .radio-custom .v-messages {
  display: none;
}
.radio-input-wrapper >>> .v-radio {
  display: flex;
  justify-content: center;
}
.radio-input-wrapper >>> .v-input--radio {
  width: auto;
}
</style>
